import { formatIsoDate } from '@shared/vueltoDateTime';
import type { ExpenseListQuery, ExpenseRow, ExpenseSortBy } from '../domain/expenseTypes';
import { EXPENSE_PERIOD_PRESETS, type ExpensePeriodPreset } from '../application/expensesList.constants';
import { buildAccentInsensitiveSqlExpression, matchesSearchAny, normalizeSearchText } from '../../shared/format/searchText';

export type ExpenseListSqlParts = {
  where: string;
  args: unknown[];
  orderBy: string;
};

export const SQLITE_LOCAL_TODAY = "date('now', 'localtime')";
export const SQLITE_LOCAL_IN_7_DAYS = "date('now', 'localtime', '+7 days')";
export const SQLITE_EXPENSE_IS_OVERDUE = `e.due_date IS NOT NULL AND e.due_date < ${SQLITE_LOCAL_TODAY} AND e.payment_status != 'paid'`;

export function expenseSearchLikePattern(search: string): string {
  return `%${search.replace(/%/g, '\\%').replace(/_/g, '\\_')}%`;
}

function expenseListOrderByClause(sortBy: ExpenseSortBy | undefined): string {
  switch (sortBy) {
    case 'amount_desc':
      return 'e.amount DESC, e.expense_date DESC';
    case 'amount_asc':
      return 'e.amount ASC, e.expense_date DESC';
    case 'created_desc':
      return 'e.created_at DESC';
    case 'due_date_asc':
      return 'CASE WHEN e.due_date IS NULL THEN 1 ELSE 0 END, e.due_date ASC, e.expense_date DESC';
    case 'due_date_desc':
      return 'e.due_date DESC, e.expense_date DESC';
    case 'expense_date_desc':
    default:
      return 'e.expense_date DESC, e.created_at DESC';
  }
}

export function buildExpenseListSql(query: ExpenseListQuery): ExpenseListSqlParts {
  // 'voided' = facturas anuladas; cualquier otro filtro corre sobre las activas.
  const wantVoided = query.paymentStatus === 'voided';
  const conditions = ['e.branch_id = ?', wantVoided ? "e.status = 'voided'" : "e.status = 'active'"];
  const args: unknown[] = [query.branchId];

  if (query.fromDate) {
    conditions.push('e.expense_date >= ?');
    args.push(query.fromDate);
  }
  if (query.toDate) {
    conditions.push('e.expense_date <= ?');
    args.push(query.toDate);
  }
  if (query.paymentStatus && query.paymentStatus !== 'all' && !wantVoided) {
    conditions.push('e.payment_status = ?');
    args.push(query.paymentStatus);
  }
  if (query.categoryId) {
    conditions.push('e.category_id = ?');
    args.push(query.categoryId);
  }
  if (query.supplierId) {
    conditions.push('e.supplier_id = ?');
    args.push(query.supplierId);
  }
  if (query.dueStatus === 'overdue') {
    conditions.push(SQLITE_EXPENSE_IS_OVERDUE);
  } else if (query.dueStatus === 'due_soon') {
    conditions.push(
      `e.due_date IS NOT NULL AND e.due_date >= ${SQLITE_LOCAL_TODAY} AND e.due_date <= ${SQLITE_LOCAL_IN_7_DAYS} AND e.payment_status != 'paid'`
    );
  } else if (query.dueStatus === 'no_due_date') {
    conditions.push('e.due_date IS NULL');
  }
  const search = query.search?.trim();
  if (search) {
    const like = expenseSearchLikePattern(normalizeSearchText(search));
    conditions.push(
      `(${buildAccentInsensitiveSqlExpression('e.description')} LIKE ? ESCAPE '\\' OR ${buildAccentInsensitiveSqlExpression('e.receipt_ref')} LIKE ? ESCAPE '\\' OR ${buildAccentInsensitiveSqlExpression('s.name')} LIKE ? ESCAPE '\\' OR ${buildAccentInsensitiveSqlExpression('c.name')} LIKE ? ESCAPE '\\')`
    );
    args.push(like, like, like, like);
  }

  return {
    where: conditions.join(' AND '),
    args,
    orderBy: expenseListOrderByClause(query.sortBy),
  };
}

export function defaultExpensePeriodFrom(daysBack = 30): string {
  const d = new Date();
  d.setDate(d.getDate() - daysBack);
  return formatIsoDate(d);
}

export function todayIsoDate(): string {
  return formatIsoDate(new Date());
}

/** Bounds para el preset "Todos" (sin cota de fecha): cubren cualquier timestamp ISO. */
export const UNBOUNDED_PERIOD_ISO_RANGE = {
  fromIso: '1970-01-01T00:00:00.000Z',
  toIso: '9999-12-31T23:59:59.999Z',
} as const;

/** Inclusive local-calendar-day bounds as UTC ISO timestamps (not UTC midnight). */
export function localCalendarDayIsoRange(fromDate: string, toDate: string): { fromIso: string; toIso: string } {
  const from = fromDate.slice(0, 10);
  const to = toDate.slice(0, 10);
  if (!from || !to) {
    return { ...UNBOUNDED_PERIOD_ISO_RANGE };
  }
  return {
    fromIso: new Date(`${from}T00:00:00`).toISOString(),
    toIso: new Date(`${to}T23:59:59.999`).toISOString(),
  };
}

export function resolvePeriodDates(
  preset: ExpensePeriodPreset,
  fromDate: string,
  toDate: string
): { fromDate: string; toDate: string } {
  if (preset === 'all') {
    // Sin cota: strings vacíos — los consumidores los tratan como "sin filtro".
    return { fromDate: '', toDate: '' };
  }
  if (preset === 'custom') {
    if (fromDate && toDate && fromDate > toDate) {
      return { fromDate: toDate, toDate: fromDate };
    }
    return { fromDate, toDate };
  }
  const match = EXPENSE_PERIOD_PRESETS.find((p) => p.id === preset);
  const days = match?.days ?? 30;
  return { fromDate: defaultExpensePeriodFrom(days), toDate: todayIsoDate() };
}

/**
 * Filtro en memoria sobre ExpenseRow — espejo de buildExpenseListSql para el
 * path de cache web (RAM). 'voided' lista facturas anuladas; cualquier otro
 * filtro de paymentStatus corre solo sobre activas.
 */
export function matchesExpenseListQuery(row: ExpenseRow, query: ExpenseListQuery): boolean {
  if (row.branchId !== query.branchId) return false;
  if (query.paymentStatus === 'voided') {
    if (row.status !== 'voided') return false;
  } else {
    if (row.status !== 'active') return false;
    if (query.paymentStatus && query.paymentStatus !== 'all' && row.paymentStatus !== query.paymentStatus) {
      return false;
    }
  }
  if (query.fromDate && row.expenseDate < query.fromDate) return false;
  if (query.toDate && row.expenseDate > query.toDate) return false;
  if (query.categoryId && row.categoryId !== query.categoryId) return false;
  if (query.supplierId && row.supplierId !== query.supplierId) return false;
  const today = todayIsoDate();
  if (query.dueStatus === 'overdue') {
    if (!(row.dueDate && row.dueDate < today && row.paymentStatus !== 'paid')) return false;
  } else if (query.dueStatus === 'due_soon') {
    const in7 = (() => {
      const d = new Date(`${today}T12:00:00`);
      d.setDate(d.getDate() + 7);
      return d.toISOString().slice(0, 10);
    })();
    if (!(row.dueDate && row.dueDate >= today && row.dueDate <= in7 && row.paymentStatus !== 'paid')) {
      return false;
    }
  } else if (query.dueStatus === 'no_due_date' && row.dueDate != null) {
    return false;
  }
  if (query.search?.trim()) {
    if (!matchesSearchAny([row.description, row.receiptRef, row.supplierName, row.categoryName], query.search)) {
      return false;
    }
  }
  return true;
}
